import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { counterpartyContract } from "@common/contracts/counterparty.contract";
import type { Counterparty } from "@common/types/counterparty";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { counterpartiesQueryKeys } from "./counterparty.api";

const counterpartyContractClient = getBackendAPIClient(counterpartyContract);

async function fetchCounterpartyIfExists({
    workspaceId,
    counterpartyId,
}: {
    workspaceId: string;
    counterpartyId: string;
}): Promise<Counterparty | null> {
    try {
        const response =
            await counterpartyContractClient.getCounterpartyDetails({
                params: { workspaceId, counterpartyId },
                // a removed counterparty is an expected 404, not an error to toast
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
        return response.body.counterparty;
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        throw error;
    }
}

/**
 * Looks up one counterparty by id for a chat pill. `data` is `null` once the
 * server says it no longer exists. The key extends the by-id key, so edits
 * that invalidate a counterparty's details refresh the pill too.
 */
export function useCounterpartyRefQuery(counterpartyId: string) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: [
            ...counterpartiesQueryKeys.byId(workspaceId ?? "", counterpartyId),
            "chat-ref",
        ],
        enabled: Boolean(workspaceId),
        queryFn: () => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            return fetchCounterpartyIfExists({ workspaceId, counterpartyId });
        },
    });
}
